/**
 * EE API Roles 管理頁（Enterprise Edition only）
 *
 * 提供 API 角色 CRUD。對應後端：/api/ee/api_roles/*
 * 注意：此「API 角色」與 CE 的 user_roles（真人角色）完全分離。
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper,
  Tooltip, Typography, Stack,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
         Refresh as RefreshIcon } from '@mui/icons-material';

import FunctionPageHeader from '../../components/FunctionPageHeader';
import { useMessage } from '../../contexts/MessageContext';
import { useSystem } from '../../contexts/SystemContext';
import { getI18nValue } from '../../utils/i18nHelper';
import {
  ApiRole, ApiRoleCreate, listApiRoles, createApiRole,
  updateApiRole, deleteApiRole,
} from '../../services/eeApiRolesService';
import { formatLocal } from '../../utils/dateHelper';


const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try { return formatLocal(iso); } catch { return iso; }
};

const emptyForm: ApiRoleCreate = {
  role_code: '',
  role_name: {},
  description: '',
  is_active: true,
};


const EeApiRolesPage: React.FC = () => {
  const { i18n } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { availableLanguages } = useSystem();
  const availableLangs = (availableLanguages || []).map(l => l.code);

  const [roles, setRoles] = useState<ApiRole[]>([]);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ApiRole | null>(null);
  const [form, setForm] = useState<ApiRoleCreate>(emptyForm);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(await listApiRoles());
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  }, [showApiError]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setEditing(null);
    const init: ApiRoleCreate = { ...emptyForm, role_name: {} };
    availableLangs.forEach(l => { init.role_name[l] = ''; });
    setForm(init);
    setEditOpen(true);
  };

  const openEdit = (role: ApiRole) => {
    setEditing(role);
    const names = { ...(role.role_name || {}) };
    availableLangs.forEach(l => { if (!(l in names)) names[l] = ''; });
    setForm({
      role_code: role.role_code,
      role_name: names,
      description: role.description || '',
      is_active: role.is_active,
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await updateApiRole(editing.id, form);
        showSuccess('SYS040002', { name: form.role_code });
      } else {
        await createApiRole(form);
        showSuccess('SYS040001', { name: form.role_code });
      }
      setEditOpen(false);
      reload();
    } catch (err) { showApiError(err); }
  };

  const handleDelete = async (role: ApiRole) => {
    if (!window.confirm(`確定刪除角色「${getI18nValue(role.role_name, i18n.language) || role.role_code}」？`)) return;
    try {
      await deleteApiRole(role.id);
      showSuccess('SYS040003', { name: role.role_code });
      reload();
    } catch (err) { showApiError(err); }
  };

  return (
    <Box>
      <FunctionPageHeader funcCode="ee_api_roles" fallback="API 角色管理" />
      <Stack direction="row" spacing={1} sx={{ my: 2 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          新增
        </Button>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={reload}
                disabled={loading}>
          重整
        </Button>
        <Typography variant="body2" sx={{ ml: 'auto', alignSelf: 'center', color: 'text.secondary' }}>
          共 {roles.length} 個角色
        </Typography>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>role_code</TableCell>
              <TableCell>名稱</TableCell>
              <TableCell>說明</TableCell>
              <TableCell align="center">啟用</TableCell>
              <TableCell align="center">綁 Key 數</TableCell>
              <TableCell>最後更新</TableCell>
              <TableCell align="right">動作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.id}</TableCell>
                <TableCell><code>{r.role_code}</code></TableCell>
                <TableCell>{getI18nValue(r.role_name, i18n.language) || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 300 }} title={r.description || ''}>
                  {r.description || '—'}
                </TableCell>
                <TableCell align="center">
                  <Chip label={r.is_active ? '啟用' : '停用'} size="small"
                        color={r.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <Chip label={r.key_count} size="small"
                        color={r.key_count > 0 ? 'primary' : 'default'} />
                </TableCell>
                <TableCell>{formatDateTime(r.updated_at)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="編輯">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={r.key_count > 0 ? `有 ${r.key_count} 把 Key 綁定，無法刪除` : '刪除'}>
                    <span>
                      <IconButton size="small" onClick={() => handleDelete(r)}
                                  disabled={r.key_count > 0}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                {loading ? '載入中…' : '（尚無資料）'}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 新增/編輯 Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? '編輯 API 角色' : '新增 API 角色'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="role_code（程式識別碼）" size="small" fullWidth required
                       value={form.role_code}
                       onChange={e => setForm({ ...form, role_code: e.target.value })}
                       disabled={!!editing}
                       helperText={editing ? '建立後不可修改（影響 role_rights 查詢）' : '建議以 api_ 開頭，如 api_erp_integration'} />
            {availableLangs.map(lang => (
              <TextField key={lang} label={`名稱 [${lang}]`} size="small" fullWidth
                         value={form.role_name[lang] || ''}
                         onChange={e => setForm({
                           ...form,
                           role_name: { ...form.role_name, [lang]: e.target.value },
                         })} />
            ))}
            <TextField label="說明" size="small" fullWidth multiline minRows={2}
                       value={form.description || ''}
                       onChange={e => setForm({ ...form, description: e.target.value })} />
            <FormControlLabel label="啟用"
                              control={<Switch checked={!!form.is_active}
                                onChange={e => setForm({ ...form, is_active: e.target.checked })} />} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>取消</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.role_code}>
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EeApiRolesPage;